import React, { createContext, useContext, useEffect, useState } from "react";
import {
    createUserWithEmailAndPassword,
    deleteUser,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    updateProfile,
    User,
} from "firebase/auth";
import {
    collection,
    deleteDoc,
    doc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    setDoc,
    where,
    getDoc,
} from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { ProductReview, UserProfile } from "../types/user";
import { evaluateAdultBirthDate } from "../utils/age-rule.mjs";

type RegisterInput = {
    name: string;
    username: string;
    birthDate: string;
    email: string;
    password: string;
};

type ReviewInput = {
    productShortcode: string;
    productTitle: string;
    rating: number;
    review: string;
};

type AuthContextValue = {
    user: User | null;
    profile: UserProfile | null;
    reviews: ProductReview[];
    loading: boolean;
    register: (input: RegisterInput) => Promise<void>;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    toggleLike: (productShortcode: string) => Promise<void>;
    toggleGrowerLike: (growerShortcode: string) => Promise<void>;
    toggleShopLike: (shopShortcode: string) => Promise<void>;
    saveReview: (input: ReviewInput) => Promise<void>;
    deleteReview: (productShortcode: string) => Promise<void>;
    setPushEnabled: (enabled: boolean) => Promise<void>;
    updateProfileDetails: (input: {
        displayName?: string;
        username?: string;
        avatarUrl?: string;
        headerImageUrl?: string;
        bio?: string;
    }) => Promise<void>;
    isProductLiked: (productShortcode: string) => boolean;
    isGrowerLiked: (growerShortcode: string) => boolean;
    isShopLiked: (shopShortcode: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const buildDefaultProfile = (currentUser: User): UserProfile => {
    const displayName = currentUser.displayName ?? currentUser.email?.split("@")[0] ?? "Gebruiker";
    return {
        uid: currentUser.uid,
        email: currentUser.email ?? "",
        displayName,
        username: displayName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""),
        avatarUrl: currentUser.photoURL ?? "",
        headerImageUrl: "",
        bio: "",
        pushEnabled: false,
        likedProducts: [],
        likedGrowers: [],
        likedShops: [],
    };
};

const ensureProfileDoc = async (currentUser: User) => {
    const profileRef = doc(db, "Gebruikers", currentUser.uid);
    const existingProfile = await getDoc(profileRef);
    if (!existingProfile.exists()) {
        const defaultProfile = buildDefaultProfile(currentUser);
        await setDoc(
            profileRef,
            {
                ...defaultProfile,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );
    }
};

function normalizeUsername(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

async function ensureUsernameAvailable(username: string, excludeUid?: string) {
    const normalized = normalizeUsername(username);
    if (!normalized) {
        throw new Error("Kies een geldige gebruikersnaam.");
    }

    const existing = await getDocs(query(collection(db, "Gebruikers"), where("username", "==", normalized)));
    const conflict = existing.docs.find((docItem) => docItem.id !== excludeUid);
    if (conflict) {
        throw new Error("Deze gebruikersnaam is al in gebruik.");
    }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [reviews, setReviews] = useState<ProductReview[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });

        return () => unsubscribeAuth();
    }, []);

    useEffect(() => {
        if (!user) {
            setProfile(null);
            setReviews([]);
            return;
        }

        const profileRef = doc(db, "Gebruikers", user.uid);
        ensureProfileDoc(user).catch((error) => {
            console.warn("Profieldocument kon niet automatisch worden aangemaakt.", error);
        });

        const unsubscribeProfile = onSnapshot(profileRef, (snapshot) => {
            if (!snapshot.exists()) {
                return;
            }

            const data = snapshot.data() as UserProfile;
            setProfile({
                ...buildDefaultProfile(user),
                ...data,
                uid: user.uid,
            });
        });

        const reviewQuery = query(
            collection(db, "reviews"),
            where("userId", "==", user.uid)
        );
        const unsubscribeReviews = onSnapshot(reviewQuery, (snapshot) => {
            setReviews(
                snapshot.docs.map((item) => ({
                    id: item.id,
                    ...(item.data() as Omit<ProductReview, "id">),
                }))
            );
        });

        return () => {
            unsubscribeProfile();
            unsubscribeReviews();
        };
    }, [user]);

    const register = async ({ name, username, birthDate, email, password }: RegisterInput) => {
        if (!navigator.onLine) {
            throw new Error("Registreren kan alleen wanneer je online bent.");
        }

        if (!evaluateAdultBirthDate(birthDate).allowed) throw new Error("Registratie is alleen mogelijk vanaf 18 jaar.");
        const normalizedUsername = normalizeUsername(username);
        await ensureUsernameAvailable(normalizedUsername);

        const credential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(credential.user, { displayName: name });
        try {
            await setDoc(doc(db, "Gebruikers", credential.user.uid), {
                uid: credential.user.uid,
                email,
                displayName: name,
                username: normalizedUsername,
                avatarUrl: "",
                headerImageUrl: "",
                ageVerified: true,
                ageVerifiedAt: serverTimestamp(),
                bio: "",
                pushEnabled: false,
                likedProducts: [],
                likedGrowers: [],
                likedShops: [],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
        } catch (error) {
            try {
                await deleteUser(credential.user);
            } catch (cleanupError) {
                console.error("Opschonen van auth-user na mislukte profielwrite mislukte.", cleanupError);
            }
            throw new Error("Registratie mislukt: profiel kon niet in Firestore worden opgeslagen.");
        }
    };

    const login = async (email: string, password: string) => {
        if (!navigator.onLine) {
            throw new Error("Inloggen kan alleen wanneer je online bent.");
        }
        await signInWithEmailAndPassword(auth, email, password);
    };

    const logout = async () => {
        await signOut(auth);
    };

    const toggleLike = async (productShortcode: string) => {
        if (!user || !profile) {
            throw new Error("Log in om soortjes te liken.");
        }

        const normalizedShortcode = productShortcode?.trim();
        if (!normalizedShortcode) {
            throw new Error("Dit product kan niet geliket worden omdat er geen shortcode beschikbaar is.");
        }

        if (!navigator.onLine) {
            throw new Error("Likes bijwerken kan alleen wanneer je online bent.");
        }

        const userRef = doc(db, "Gebruikers", user.uid);
        const currentLiked = Array.isArray(profile.likedProducts) ? profile.likedProducts : [];
        const uniqueLiked = Array.from(new Set(currentLiked.filter(Boolean)));
        const isLiked = uniqueLiked.includes(normalizedShortcode);

        const nextLiked = isLiked
            ? uniqueLiked.filter((item) => item !== normalizedShortcode)
            : [...uniqueLiked, normalizedShortcode];

        await setDoc(
            userRef,
            {
                likedProducts: nextLiked,
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );
    };

    const saveReview = async (input: ReviewInput) => {
        if (!user || !profile) {
            throw new Error("Log in om reviews te plaatsen.");
        }

        if (!navigator.onLine) {
            throw new Error("Reviews plaatsen kan alleen wanneer je online bent.");
        }

        if (reviews.some((item) => item.productShortcode === input.productShortcode)) {
            throw new Error("Je hebt al een review geplaatst. Verwijder die eerst.");
        }

        const reviewId = `${user.uid}_${input.productShortcode}`;
        const reviewRef = doc(db, "reviews", reviewId);
        await setDoc(reviewRef, {
            userId: user.uid,
            userName: profile.displayName,
            productShortcode: input.productShortcode,
            productTitle: input.productTitle,
            rating: input.rating,
            review: input.review,
            reactions: {},
            upVotes: 0,
            downVotes: 0,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
        });
    };

    const toggleGrowerLike = async (growerShortcode: string) => {
        if (!user || !profile) {
            throw new Error("Log in om telers te liken.");
        }

        const normalizedShortcode = growerShortcode?.trim();
        if (!normalizedShortcode) {
            throw new Error("Deze teler kan niet geliket worden omdat er geen shortcode beschikbaar is.");
        }

        if (!navigator.onLine) {
            throw new Error("Likes bijwerken kan alleen wanneer je online bent.");
        }

        const userRef = doc(db, "Gebruikers", user.uid);
        const currentLiked = Array.isArray(profile.likedGrowers) ? profile.likedGrowers : [];
        const uniqueLiked = Array.from(new Set(currentLiked.filter(Boolean)));
        const isLiked = uniqueLiked.includes(normalizedShortcode);

        const nextLiked = isLiked
            ? uniqueLiked.filter((item) => item !== normalizedShortcode)
            : [...uniqueLiked, normalizedShortcode];

        await setDoc(
            userRef,
            {
                likedGrowers: nextLiked,
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );
    };

    const toggleShopLike = async (shopShortcode: string) => {
        if (!user || !profile) {
            throw new Error("Log in om winkels te liken.");
        }

        const normalizedShortcode = shopShortcode?.trim();
        if (!normalizedShortcode) {
            throw new Error("Deze winkel kan niet geliket worden omdat er geen shortcode beschikbaar is.");
        }

        if (!navigator.onLine) {
            throw new Error("Likes bijwerken kan alleen wanneer je online bent.");
        }

        const userRef = doc(db, "Gebruikers", user.uid);
        const currentLiked = Array.isArray(profile.likedShops) ? profile.likedShops : [];
        const uniqueLiked = Array.from(new Set(currentLiked.filter(Boolean)));
        const isLiked = uniqueLiked.includes(normalizedShortcode);

        const nextLiked = isLiked
            ? uniqueLiked.filter((item) => item !== normalizedShortcode)
            : [...uniqueLiked, normalizedShortcode];

        await setDoc(
            userRef,
            {
                likedShops: nextLiked,
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );
    };

    const deleteReview = async (productShortcode: string) => {
        if (!user) {
            throw new Error("Log in om reviews te verwijderen.");
        }

        if (!navigator.onLine) {
            throw new Error("Reviews verwijderen kan alleen wanneer je online bent.");
        }

        const reviewId = `${user.uid}_${productShortcode}`;
        await deleteDoc(doc(db, "reviews", reviewId));
    };

    const setPushEnabled = async (enabled: boolean) => {
        if (!user) {
            throw new Error("Log in om pushmeldingen te beheren.");
        }

        if (!navigator.onLine) {
            throw new Error("Push-instellingen aanpassen kan alleen wanneer je online bent.");
        }

        if (enabled) {
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                throw new Error("Pushmeldingen zijn niet toegestaan in deze browser.");
            }
        }

        await setDoc(
            doc(db, "Gebruikers", user.uid),
            {
                pushEnabled: enabled,
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );
    };

    const updateProfileDetails = async (input: {
        displayName?: string;
        username?: string;
        avatarUrl?: string;
        headerImageUrl?: string;
        bio?: string;
    }) => {
        if (!user) {
            throw new Error("Log in om profielgegevens bij te werken.");
        }

        if (!navigator.onLine) {
            throw new Error("Profiel aanpassen kan alleen wanneer je online bent.");
        }

        const payload: Record<string, unknown> = {
            updatedAt: serverTimestamp(),
        };

        if (input.displayName !== undefined) payload.displayName = input.displayName;
        if (input.username !== undefined) {
            const normalizedUsername = normalizeUsername(input.username);
            await ensureUsernameAvailable(normalizedUsername, user.uid);
            payload.username = normalizedUsername;
        }
        if (input.avatarUrl !== undefined) payload.avatarUrl = input.avatarUrl;
        if (input.headerImageUrl !== undefined) payload.headerImageUrl = input.headerImageUrl;
        if (input.bio !== undefined) payload.bio = input.bio;

        await setDoc(doc(db, "Gebruikers", user.uid), payload, { merge: true });
    };

    const isProductLiked = (productShortcode: string) =>
        Boolean(profile?.likedProducts?.includes(productShortcode));
    const isGrowerLiked = (growerShortcode: string) =>
        Boolean(profile?.likedGrowers?.includes(growerShortcode));
    const isShopLiked = (shopShortcode: string) =>
        Boolean(profile?.likedShops?.includes(shopShortcode));

    const value = {
        user,
        profile,
        reviews,
        loading,
        register,
        login,
        logout,
        toggleLike,
        toggleGrowerLike,
        toggleShopLike,
        saveReview,
        deleteReview,
        setPushEnabled,
        updateProfileDetails,
        isProductLiked,
        isGrowerLiked,
        isShopLiked,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth moet binnen AuthProvider gebruikt worden.");
    }
    return context;
}
